import { useEffect } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { api } from "../api/client.ts";
import { useMe } from "../api/queries.ts";
import { Button, ErrorBlock, Spinner } from "../components/ui.tsx";

/**
 * 分销商专属链接的落地页 —— **整个裂变机制的入口**。
 *
 * 🔴 这一步**只记访问，不绑定**：用户还没注册，没有 user_id 可绑。
 *    绑定发生在注册成功那一刻，所以邀请码和 visitId 要一路带到注册页。
 *
 * ⚠️ 已登录的用户点进来不会被改归属（后端默认先绑定者优先），
 *    所以直接放行到首页，不打断他。
 */
function Invite() {
  const { code } = useParams({ from: "/i/$code" });
  const navigate = useNavigate();
  const { data: me, isPending: mePending } = useMe();

  const { data, isPending, error } = useQuery({
    queryKey: ["invite", code],
    queryFn: () =>
      api.get<{ visitId: number; org: { id: number; name: string; type: string } }>(
        `/auth/invite/${code}`,
      ),
    retry: false,
  });

  /**
   * 🔴 **落到店铺页，不是首页。**
   *    以前已登录的用户点进来直接跳首页，那这个链接跟"随便发个商城地址"没区别 ——
   *    分享的人想让你看的是**他的店**。
   *
   * ⚠️ 已登录且已有归属的人不会被改归属（先绑定者优先），
   *    但他仍然应该看到这家店 —— 店铺页会明说"你的专属服务商是 XX、价格按他算"。
   */
  useEffect(() => {
    if (!mePending && me && data?.org.id) {
      navigate({ to: "/shop/$id", params: { id: String(data.org.id) }, replace: true });
    }
  }, [me, mePending, data, navigate]);

  if (isPending || mePending) return <Spinner label="正在打开商城" />;

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[420px] px-4 py-16">
        <ErrorBlock error={error} />
        <Button variant="outline" className="mx-auto mt-4 block" onClick={() => navigate({ to: "/" })}>
          去平台首页
        </Button>
      </div>
    );
  }

  return (
    /*
      ⚠️ **这一页两边是同一种形态**：一张居中的单任务卡，
         手机上占满屏、电脑上收在 420 —— 不收的话按钮会横贯 1440 的屏幕，
         那是"拉伸"不是适配。所以这里不另开一套页面，只限一个宽度。
    */
    <div
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col justify-center px-6"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-2xl bg-brand-50 text-xl text-brand-700">
          {data.org.name.slice(0, 2)}
        </div>
        <h1 className="mt-4 text-xl font-medium">{data.org.name}</h1>
        <p className="mt-1 text-sm text-ink-500">邀请你来选建材</p>
      </div>

      {/*
        🔴 **只有「注册」一条路，不给「先逛逛」。**（老板指定）

           以前这儿还有一颗「先逛逛他的店」。想法是"看中了再注册，链路更短"，
           但它把邀请链接**唯一要做成的那件事**变成了可选项 ——
           归属是这个平台的核心资产，人从这条链接进来却没注册，
           商家等于白发了一次链接。

        ⚠️ 已经有账号的人不会卡住：登录页上有「已有账号？去登录」，
           而且 `invite` 和 `redirect` 会一路带过去，登完照样落到这家店。
      */}
      <div className="mt-10">
        <Button
          size="lg"
          className="w-full"
          onClick={() =>
            navigate({
              to: "/login",
              search: { invite: code, visitId: data.visitId, redirect: `/shop/${data.org.id}` },
            })
          }
        >
          注册看价
        </Button>
      </div>

      <p className="mt-6 text-center text-xs text-ink-500">
        注册后由 {data.org.name} 为你服务
      </p>
    </div>
  );
}

export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/i/$code",
  component: Invite,
});
